import { ChangeDetectionStrategy, Component, booleanAttribute, computed, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationSkipped, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { filterMap } from 'fp-ts-rxjs/Observable';
import { none, some } from 'fp-ts/Option';
import { withLatestFrom } from 'rxjs';

import { FrameService } from '../../../../iframe/frame.service';
import { LayoutTranslation } from '../layout-translation';
import { MenuService } from '../menu.service';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'dmb-submenu-item',
  templateUrl: './submenu-item.component.html',
  styleUrls: ['./submenu-item.component.scss'],
  imports: [RouterLink, RouterLinkActive],
  host: {
    '[class.active]': 'active()',
    '[class.disabled]': '!enabled()',
  },
})
export class SubmenuItemComponent {
  private menuService = inject(MenuService);

  private tr = LayoutTranslation.getTranslations();
  path = input('');
  alwaysEnabled = input(false, { transform: booleanAttribute });
  key = input.required<keyof LayoutTranslation>();
  queryParams = input<{ [key: string]: string | number }>({});
  watchClick = input(false);

  active = signal(false);

  label = computed(() => this.tr[this.key()] ?? this.path());

  enabled = computed(() => {
    if (this.alwaysEnabled()) {
      return true;
    }

    return this.menuService.menu()?.includes(this.key()) ?? false;
  });

  constructor() {
    const router = inject(Router);
    const v3 = inject(FrameService);

    router.events
      .pipe(
        filterMap((e) =>
          this.watchClick() && this.active() && e instanceof NavigationSkipped ? some(this.queryParams()) : none,
        ),
        withLatestFrom(v3.frameAvailable),
        filterMap(([q, active]) => (active ? some(q) : none)),
        takeUntilDestroyed(),
      )
      .subscribe((payload) => v3.postMessage('overlay-request', payload));
  }

  deactivate() {
    this.menuService.setInactive();
  }
}
